import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppStoreContext } from '../../context/appStoreContext';
import { checkLockStatus } from '../../utils/services/complexDocumentsAPI';
import DeleteTemplateModal from '../../components/Modals/DeleteTemplateModal';
import TemplateLockedModal from '../ComplexDocumentDesign/TemplateLockedModal';
import PageNotFoundIllustration from '../../images/generatingPreview.png';
import moment from 'moment';
import styled from 'styled-components';
import { Dropdown, Menu, Icon, Tooltip, message, Popover } from 'antd';
import { GlobeIcon } from '../../images/svgIcons';
import { NEW_TEMPLATE_SECTION } from './translationValues';
import URL from '../../utils/helper';

const Card = styled.div`
  border: 1px solid #f3f4f6;
  border-radius: 16px;
  background: white;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: all 0.3s;
  &:hover {
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  }
`;

const PreviewSection = styled.div`
  border-bottom: 1px solid #e0dada;
  position: relative;
  aspect-ratio: 3/4;
  display: grid;
  place-items: center;
  background: #fff;
  overflow: hidden;
  cursor: pointer;
`;

const PreviewImage = styled.img`
  transition: transform 0.5s;
  ${Card}:hover & {
    transform: scale(1.1);
  }
`;

const GradientOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.6), transparent, transparent);
  opacity: 0;
  transition: opacity 0.3s;
  ${Card}:hover & {
    opacity: 1;
  }
`;

const LanguageBadge = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  border-radius: 12px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 8px;
  svg {
    color: #4f46e5;
  }
  span {
    font-size: 12px;
  }
`;

const TopActions = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  gap: 8px;
  opacity: ${props => (props.visible ? 1 : 0)};
  transform: ${props => (props.visible ? 'translateY(0)' : 'translateY(-8px)')};
  transition: all 0.3s;
`;

const ActionButton = styled.button`
  border: none;
  border-radius: 12px;
  height: 36px;
  width: 36px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.2s;
  position: relative;
  &:hover {
    transform: scale(1.1);
  }
  &:hover {
    opacity: 1;
    visibility: visible;
  }
  svg {
    color: #4b5563;
  }
`;

const BottomActions = styled.div`
  position: absolute;
  right: 8px;
  bottom: 8px;
  left: 8px;
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
`;

const PageDate = styled.div`
  border-radius: 12px;
  width: 60px;
  padding: 6px 12px;
  background: rgb(243, 242, 245);
  font-size: 12px;
  text-align: center;
  p {
    font-size: 18px;
    font-weight: 600;
    color: #000;
  }
  span {
    font-weight: 600;
    text-transform: uppercase;
    color: rgb(136, 136, 136);
  }
`;

const InfoSection = styled.div`
  padding: 16px;
  .badge_wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
`;

const CardTitle = styled.h3`
  margin-bottom: 12px;
  font-size: 14px;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.2s;
  ${Card}:hover & {
    color: #4f46e5;
  }
`;

const CategoryBadge = styled.div`
  border: 1px solid #5eead4;
  border-radius: 12px;
  padding: 6px 12px;
  max-width: 100%;
  font-size: 12px;
  background: linear-gradient(to right, #f0fdfa, #ccfbf1);
  color: #0f766e;
  display: inline-block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
`;

const LanguageSelector = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
`;

const LanguageButton = styled.button`
  border: 1px solid;
  border-radius: 12px;
  padding: 6px 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  &:hover {
    opacity: 1;
    visibility: visible;
  }
  ${props => {
    if (props.selected) {
      return `
      border-color: transparent;
        background: linear-gradient(to right, #6366f1, #a855f7);
        color: white;
        box-shadow: 0 2px 4px 0 rgba(99, 102, 241, 0.3);
        transform: scale(1.05);
      `;
    } else if (props.highlighted) {
      return `
      border-color: #a5b4fc;
        background: linear-gradient(to right, #e0e7ff, #ede9fe);
        color: #4f46e5;
      `;
    } else {
      return `
      border-color: #e5e7eb;
        background: #f3f4f6;
        color: #4b5563;
        
        &:hover {
          background: #e5e7eb;
        }
      `;
    }
  }}
  span:first-child {
    font-size: 16px;
    line-height: 1;
  }
  span:last-child {
    text-transform: uppercase;
  }
`;

const SingleLanguageBadge = styled.div`
  border: 1px solid #c7d2fe;
  border-radius: 12px;
  padding: 6px 12px;
  font-size: 12px;
  text-transform: uppercase;
  background: linear-gradient(to right, #eef2ff, #f5f3ff);
  color: #4f46e5;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  span:first-child {
    font-size: 16px;
    line-height: 1;
  }
`;

const LanguagePopover = styled.div`
  max-width: 320px;
  .popover_header {
    margin-bottom: 8px;
    border-bottom: 1px solid #f3f4f6;
    padding: 0 0 8px;
    font-size: 11px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .popover_body {
    max-height: 250px;
    padding-inline: 2px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    overflow-y: auto;
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
    ::-webkit-scrollbar {
      display: none;
    }
  }
`;

const languageFlags = {
  en: '🇬🇧',
  es: '🇪🇸',
  fr: '🇫🇷',
  de: '🇩🇪',
  it: '🇮🇹',
  pt: '🇵🇹',
  zh: '🇨🇳',
  ja: '🇯🇵',
};

const OverflowTooltip = ({ children, title }) => {
  const [truncated, setTruncated] = useState(false);

  const handleMouseEnter = e => {
    if (e.target.scrollWidth > e.target.clientWidth) {
      setTruncated(true);
    } else {
      setTruncated(false);
    }
  };

  return (
    <Tooltip title={truncated ? title : ''}>
      {React.cloneElement(children, {
        onMouseEnter: e => {
          handleMouseEnter(e);
          if (children.props.onMouseEnter) children.props.onMouseEnter(e);
        },
      })}
    </Tooltip>
  );
};

const THUMBNAIL_KEY = 'thumbnail';

const generateThumbnailUrl = (documentId, selectedView) => {
  return selectedView === 'documents'
    ? `${URL.DESIGN_SYSTEM_URL}dynamic/thumbnail/${documentId}`
    : selectedView === 'reusable_pages'
    ? `${URL.DESIGN_SYSTEM_URL}standalone/flow/thumbnail/${documentId}`
    : `${URL.DESIGN_SYSTEM_URL}standalone/block/thumbnail/${documentId}`;
};

export function DocumentCard({
  selectedView,
  categoryList,
  template,
  highlightLanguage,
  setRefreshPage,
  allLanguages,
}) {
  const { t } = useTranslation(['NEW_TEMPLATE_SECTION']);
  const [isHovered, setIsHovered] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(
    highlightLanguage || template?.templates?.[0]?.language || template.language,
  );

  const [lockedBy, setLockedBy] = useState(null);
  const [modalVisibility, setModalVisibility] = useState(null);
  const [showLanguagePopup, setShowLanguagePopup] = useState(false);
  const [firstPageImageLink, setFirstPageImageLink] = useState(
    template?.[THUMBNAIL_KEY] ? template?.[THUMBNAIL_KEY] : undefined,
  );

  const { userDetails, checkUserAccessRoles } = useContext(AppStoreContext);

  const history = useHistory();

  useEffect(() => {
    const thumbnail =
      selectedView === 'documents' ? currentVariant?.[THUMBNAIL_KEY] : template?.[THUMBNAIL_KEY];
    if (!thumbnail) {
      setFirstPageImageLink(
        generateThumbnailUrl(
          currentVariant?.templateId || template?.id || template?.parentBlockId || template?.blockId,
          selectedView,
        ),
      );
    }
  }, [template, selectedLanguage]);

  const sortedLanguages = React.useMemo(() => {
    if (!template?.templates) return [];
    const validTemplates = template.templates.filter(v => v.language);

    return [...validTemplates].sort((a, b) => {
      if (a.language === highlightLanguage) return -1;
      if (b.language === highlightLanguage) return 1;
      return 0;
    });
  }, [template?.templates, highlightLanguage]);

  const visibleLanguages = React.useMemo(() => {
    if (sortedLanguages.length <= 3) return sortedLanguages;

    const topThree = sortedLanguages.slice(0, 3);
    const isSelectedInTopThree = topThree.some(t => t.language === selectedLanguage);

    if (isSelectedInTopThree) {
      return topThree;
    }

    const selectedVariant = sortedLanguages.find(t => t.language === selectedLanguage);
    if (selectedVariant) {
      return [topThree[0], topThree[1], selectedVariant];
    }

    return topThree;
  }, [sortedLanguages, selectedLanguage]);

  const LanguagePopoverContent = ({ t }) => (
    <LanguagePopover>
      <div className="popover_header">
        {sortedLanguages.length}{' '}
        {t(
          NEW_TEMPLATE_SECTION.LANGUAGES_AVAILABLE.key,
          NEW_TEMPLATE_SECTION.LANGUAGES_AVAILABLE.value,
        )}
      </div>
      <div className="popover_body">
        {sortedLanguages.map(variant => (
          <LanguageButton
            key={variant.language}
            selected={selectedLanguage === variant.language}
            highlighted={highlightLanguage === variant.language}
            onClick={() => {
              setSelectedLanguage(variant.language);
              setShowLanguagePopup(false);
            }}
          >
            <span>{languageFlags[variant.language] || '🌐'}</span>
            <span>
              {allLanguages?.find(ln => ln.code === variant.language)?.name || variant.language}
            </span>
          </LanguageButton>
        ))}
      </div>
    </LanguagePopover>
  );

  const redirectToTemplate = id => {
    if (selectedView === 'documents') {
      history.push(`/design/${id}`);
    } else if (selectedView === 'reusable_pages') {
      history.push(`/flow/${id}`);
    } else {
      history.push(`/block/${id}`);
    }
  };

  const currentVariant =
    template?.templates?.find(v => v.language === selectedLanguage) || template?.templates?.[0];

  const categoryName = categoryList.find(category => category.categoryId === template.categoryId)
    ?.categoryName;

  const templateId =
    currentVariant?.templateId || template.id || template.parentBlockId || template.blockId;

  const templateName =
    template.communicationName ||
    template.flowName ||
    template.parentBlockName ||
    template.blockName;

  const handleTemplateDeletion = async () => {
    let payload = {
      resourceId:
        currentVariant?.templateId || template?.id || template?.parentBlockId || template?.blockId,
      resourceType:
        selectedView === 'documents'
          ? 'document'
          : selectedView === 'reusable_pages'
          ? 'flow'
          : 'block',
    };
    const templateStatus = await checkLockStatus(payload);
    if (templateStatus.statusCode === 741) {
      if (userDetails.id !== templateStatus.data.userId) {
        setLockedBy(templateStatus.data.userName);
        setModalVisibility('locked');
        return;
      }
    }
    setModalVisibility('delete');
  };

  const onImageError = useCallback(() => {
    setFirstPageImageLink(undefined);
  }, []);

  return (
    <>
      <Card onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
        {/* Preview */}
        <PreviewSection onClick={() => redirectToTemplate(templateId)}>
          <PreviewImage
            src={
              currentVariant?.[THUMBNAIL_KEY] ||
              template?.[THUMBNAIL_KEY] ||
              firstPageImageLink ||
              PageNotFoundIllustration
            }
            onError={onImageError}
            alt={templateName}
          />

          {/* Gradient Overlay */}
          <GradientOverlay />

          {/* Language Badge - Top Left */}
          {template?.templates?.length > 1 && (
            <LanguageBadge>
              <GlobeIcon />
              <span>
                {template?.templates?.length}{' '}
                {t(
                  NEW_TEMPLATE_SECTION.LANGUAGES_COUNT.key,
                  NEW_TEMPLATE_SECTION.LANGUAGES_COUNT.value,
                )}
              </span>
            </LanguageBadge>
          )}

          {/* Top Actions */}
          <TopActions visible={isHovered}>
            <ActionButton>
              <Dropdown
                overlay={
                  <Menu>
                    <Menu.Item onClick={() => redirectToTemplate(templateId)}>
                      {t(NEW_TEMPLATE_SECTION.EDIT.key, NEW_TEMPLATE_SECTION.EDIT.value)}
                    </Menu.Item>
                    {(selectedView === 'documents' ||
                      (['reusable_pages', 'reusable_sections'].includes(selectedView) &&
                        checkUserAccessRoles(['REUSABLECONTENTMANAGER']))) && (
                      <Menu.Item
                        onClick={e => {
                          e.domEvent.stopPropagation();
                          handleTemplateDeletion();
                        }}
                      >
                        {t(NEW_TEMPLATE_SECTION.DELETE.key, NEW_TEMPLATE_SECTION.DELETE.value)}
                      </Menu.Item>
                    )}
                  </Menu>
                }
                trigger={'click'}
              >
                <Icon type="ellipsis" size={16} onClick={e => e.stopPropagation()} />
              </Dropdown>
            </ActionButton>
          </TopActions>

          {/* Bottom Actions */}
          <BottomActions>
            {/* Page Count Badge */}
            <PageDate className="updatedon_date">
              <p>{moment(currentVariant?.updatedOn || template.updatedOn).format('D')}</p>
              <span>{moment(currentVariant?.updatedOn || template.updatedOn).format('MMM')}</span>
            </PageDate>
          </BottomActions>
        </PreviewSection>

        {/* Info */}
        <InfoSection>
          <OverflowTooltip title={templateName}>
            <CardTitle>{templateName}</CardTitle>
          </OverflowTooltip>

          <div
            className="badge_wrapper"
            style={{ flexDirection: 'column', alignItems: 'flex-start' }}
          >
            {categoryName && (
              <OverflowTooltip title={categoryName}>
                <CategoryBadge>{categoryName}</CategoryBadge>
              </OverflowTooltip>
            )}

            {/* Language Selector */}
            {template?.templates?.length > 1 ? (
              <LanguageSelector>
                {visibleLanguages.map(
                  variant =>
                    variant.language && (
                      <Tooltip
                        key={variant.language}
                        title={allLanguages?.find(ln => ln.code === variant.language)?.name}
                      >
                        <LanguageButton
                          selected={selectedLanguage === variant.language}
                          highlighted={highlightLanguage === variant.language}
                          onClick={() => setSelectedLanguage(variant.language)}
                        >
                          <span>{languageFlags[variant.language] || '🌐'}</span>
                          <span>{variant.language}</span>
                        </LanguageButton>
                      </Tooltip>
                    ),
                )}
                {sortedLanguages.length > 3 && (
                  <Popover
                    content={<LanguagePopoverContent t={t} />}
                    trigger="click"
                    visible={showLanguagePopup}
                    onVisibleChange={setShowLanguagePopup}
                    placement="bottomRight"
                    overlayClassName="language-popover"
                  >
                    <Tooltip
                      title={t(
                        NEW_TEMPLATE_SECTION.VIEW_ALL_LANGUAGES_TOOLTIP.key,
                        NEW_TEMPLATE_SECTION.VIEW_ALL_LANGUAGES_TOOLTIP.value,
                      )}
                    >
                      <LanguageButton
                        onClick={e => {
                          e.stopPropagation();
                          // Toggle handled by Popover
                        }}
                        style={{ background: '#f3f4f6', color: '#4b5563' }}
                      >
                        <span>+{sortedLanguages.length - 3}</span>
                      </LanguageButton>
                    </Tooltip>
                  </Popover>
                )}
              </LanguageSelector>
            ) : (
              (template?.templates?.[0]?.language || template.language) && (
                <Tooltip
                  title={
                    allLanguages?.find(
                      ln => ln.code === (template?.templates?.[0]?.language || template.language),
                    )?.name
                  }
                >
                  <SingleLanguageBadge>
                    <span>{languageFlags[template?.templates?.[0]?.language] || '🌐'}</span>
                    {template?.templates?.[0]?.language || template.language}
                  </SingleLanguageBadge>
                </Tooltip>
              )
            )}
          </div>
        </InfoSection>
      </Card>
      <DeleteTemplateModal
        visible={modalVisibility === 'delete'}
        onCancel={() => setModalVisibility(null)}
        targetTemplate={
          selectedView === 'documents'
            ? {
                ...currentVariant,
                id: currentVariant?.templateId,
                name: template.communicationName,
              }
            : {
                ...template,
                name: template.flowName || template.parentBlockName || template.blockName,
              }
        }
        categoryType="template"
        onSucess={() => {
          message.success(
            t(
              NEW_TEMPLATE_SECTION.TEMPLATE_DELETED.key,
              NEW_TEMPLATE_SECTION.TEMPLATE_DELETED.value,
            ),
          );
          setModalVisibility(null);
          setRefreshPage(prev => prev + 1);
        }}
        TemplateType={
          selectedView === 'reusable_pages'
            ? 'flow'
            : selectedView === 'reusable_sections'
            ? 'block'
            : ''
        }
      />
      <TemplateLockedModal
        visible={modalVisibility === 'locked'}
        onClose={() => setModalVisibility(null)}
        lockedByUser={lockedBy}
        deletion={true}
      />
    </>
  );
}
